import React, {
  useCallback, useRef, useState,
} from 'react';
import useAnchoredRect, { clampToViewport } from '../../../hooks/useAnchoredRect';
import useOutsideClose from '../../../hooks/useOutsideClose';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import {
  splitStoredItems, itemsToStoredValue, normaliseItemKey, filterItemRows, SNIPPET_ITEM_MAX_LENGTH,
} from '../../../utils/snippetItems';
import { doctorDisplayName } from '../../../constants/admission';

/**
 * ComplaintItemsField (DISCHARGE-TEMPLATES 1b Slice 1 · COMP-FE) — the
 * Present-complaints ITEM picker: chips for the field's current items plus
 * a type-ahead input, mounted by `SummaryFields.jsx` whenever
 * `field.snippet_mode === "item"` (today only `chief_complaints`, §1.1).
 *
 * VALUE SHAPE: the field's on-the-wire value stays the ONE frozen §1.5
 * string (`"- fever\n- cough\n- cold"`) — this component never introduces a
 * second shape. Chips are DERIVED from `value` every render via
 * `splitStoredItems` (bk-1295, B1 fix — the LOSSLESS parser: splits on
 * comma/newline, strips a leading "- ", trims, drops only truly empty
 * pieces, KEEPS an over-120 piece and KEEPS duplicates exactly as stored)
 * — there is no local chip state to drift out of sync with the parent's
 * `value`, and no legacy piece is ever silently dropped by merely
 * rendering it. `onChange` is called ONLY when the doctor actually adds or
 * removes a chip — never on mount, never as a side effect of merely
 * rendering a legacy value (K2). Adding or removing ONE chip re-serialises
 * EVERY other stored piece byte-identical, in order (`itemsToStoredValue`
 * is handed the full current `items` array with just that one chip
 * added/removed — never a filtered/capped/deduped copy of it).
 *
 * `splitComplaintItems` (`snippetItems.js`) is a SEPARATE, LEARNABLE view
 * (≤120 chars, case-insensitive dedupe) that mirrors the backend's
 * `split_complaint_items` byte-for-byte — it is used ONLY for
 * suggestion-dedupe / "already added" identity checks (via the shared
 * `normaliseItemKey`), NEVER to build this field's stored value.
 *
 * COMMIT: Enter or a typed comma commits the current draft text. A single
 * commit can add MORE THAN ONE chip in one go — "fever, cough, cold" +
 * Enter splits into three (ruling K headline case), via the SAME
 * `splitStoredItems` the mount-time parse uses (so a chip added this way is
 * never silently dropped either). A piece over `SNIPPET_ITEM_MAX_LENGTH`
 * (120) chars IS added as a chip — it stays in the summary — but surfaces
 * an inline hint that it will not be saved as a suggestion, and that CHIP
 * itself carries a small "not saved as a suggestion" marker (bk-1295, B1 —
 * the backend's own §1.4 rule 5 only governs LEARNING, never what the
 * doctor typed into the document). Dedupe against the field's EXISTING
 * chips is case-insensitive (`normaliseItemKey`) — typing something already
 * a chip, in any case, adds nothing (a genuine NEW addition, not a legacy
 * value being merely re-rendered, so this is not a B1 data-loss concern).
 *
 * SUGGESTIONS: `suggestions` is the treating doctor's own remembered items
 * for this field (`field=chief_complaints`, §2.4's 7-key entry, reused
 * byte-identically — item mode adds zero wire keys, §1.1). Long-list rule
 * IDENTICAL to `SnippetPicker.jsx` (FE-2): first 8 rows then a "+n more"
 * row inside the same scrollable popover, a search box only once there are
 * more than 8 suggestions, live "Showing N of M" — ANCHORED popover
 * (`useAnchoredRect`/`clampToViewport`, the same pair `SnippetPicker`
 * uses), never an inline block pushed below the field. Picking a suggestion
 * row adds a chip (via the exact same dedupe path as a typed commit); the
 * row's own `×` deletes it from the doctor's remembered list (via
 * `onDeleteSuggestion`), gated on the server-computed `can_delete` and
 * ALWAYS behind `ConfirmationModal` — never a native `confirm()`.
 *
 * The FE renders a suggestion row's `text`, never its `title` — the two are
 * equal by construction on an item row (§1.2), and binding to `text` means
 * a future rename of the item never shows a stale label.
 *
 * READ-ONLY: `readOnly` (the caller's own editable/locked/permission gate —
 * `SummaryFields.jsx` derives it, this component owns no gate of its own)
 * renders the chips as PLAIN TEXT: no input, no delete, no popover.
 *
 * No "Save as template" / "Use template" trigger on this field — new
 * complaints are learned SERVER-SIDE, in the background, at summary save
 * (§1.4) — there is no client-triggered save-as-template action to offer.
 *
 * Props:
 *   fieldKey            canonical catalogue key (opaque — used only for
 *                        element ids/testids, never dispatched on)
 *   value                the field's current stored string (§1.5 shape, or
 *                        a legacy free-text value)
 *   onChange             (nextStoredValue) => void
 *   readOnly              bool
 *   ownerDoctorName      §2.4 `owner_doctor_name` (may be null while loading)
 *   suggestions           §2.4 `snippets[]` for `field=chief_complaints`
 *   loading               bool
 *   error                 string|null — already mapped to ratified copy by
 *                        the store (bk-1177 — never the server's raw
 *                        sentence)
 *   onOpenSuggestions      optional () => void, fired on closed→open,
 *                        gated on `!loading` (mirrors `SnippetPicker`'s
 *                        `onOpen`)
 *   onDeleteSuggestion     (snippetId) => Promise|void
 */

const VISIBLE_ROW_CAP = 8;

// Ratified copy (copy-ratified-slice1.md §A #12-#19), business stakeholder
// verdict 2026-09-17. Every doctor-bearing string routes through the ONE
// shared `doctorDisplayName` helper (`constants/admission.js`) so "Dr Dr"
// is structurally impossible and every string carries its own no-name
// fallback.
export const COMPLAINT_ITEMS_PLACEHOLDER = 'Type a complaint and press Enter';

/** §A #13. */
export function complaintItemsHelperCopy(ownerDoctorName) {
  const dn = doctorDisplayName(ownerDoctorName);
  return dn
    ? `Separate complaints with a comma — each one is added on its own. New complaints are saved for ${dn} and suggested next time.`
    : 'Separate complaints with a comma — each one is added on its own. New complaints are saved for the treating doctor and suggested next time.';
}

// §A #14, final wording per B1's fix (bk-1295) — an over-120 piece is now
// KEPT in the field (never rejected); this hint states only that it will
// not be learned as a suggestion.
export const COMPLAINT_ITEMS_LENGTH_HINT_COPY = `That line is longer than ${SNIPPET_ITEM_MAX_LENGTH} characters, so it will not be saved as a suggestion. It stays in the summary.`;
// Short marker rendered ON the over-120 chip itself (title/aria-label) —
// same rule as the hint above, worded for a one-line badge.
export const COMPLAINT_ITEM_NOT_SAVED_MARKER_COPY = 'Not saved as a suggestion';

export const COMPLAINT_SUGGEST_SEARCH_PLACEHOLDER = 'Search complaints…';
export const COMPLAINT_SUGGEST_EMPTY_COPY = 'No saved complaints yet. Complaints you type here are saved when you save the summary.';
export const COMPLAINT_SUGGEST_LOADING_COPY = 'Loading complaints…';

/** §A #17. */
export function complaintSuggestDeleteDisabledReason(ownerDoctorName) {
  const dn = doctorDisplayName(ownerDoctorName);
  return dn
    ? `Only ${dn} or the person who added this complaint can delete it.`
    : 'Only the treating doctor or the person who added this complaint can delete it.';
}

/** §A #18. */
function headerLine(ownerDoctorName) {
  const dn = doctorDisplayName(ownerDoctorName);
  return dn ? `${dn}'s complaints` : 'Saved complaints';
}

/** §A #19. */
function deleteConfirmMessage(item, ownerDoctorName) {
  const dn = doctorDisplayName(ownerDoctorName);
  const possessive = dn ? `${dn}'s` : "the doctor's";
  return `Remove "${item}" from ${possessive} saved complaints? This will not change the summary you are writing. It cannot be undone.`;
}

const chipCls = 'inline-flex items-center gap-1 rounded-full border border-teal-300 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 text-[12px] font-semibold pl-2.5 pr-1 py-1';
const readonlyChipCls = 'inline-flex items-center rounded-full border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[12px] font-semibold px-2.5 py-1';
const rowCls = 'flex items-start gap-2 px-2.5 py-2 border-b border-slate-100 dark:border-slate-700 last:border-b-0';
const deleteBtnCls = 'shrink-0 w-5 h-5 rounded-md border border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[11px] leading-none flex items-center justify-center disabled:opacity-60';

export default function ComplaintItemsField({
  fieldKey, value, onChange, readOnly = false, ownerDoctorName = null,
  suggestions = [], loading = false, error = null,
  onOpenSuggestions, onDeleteSuggestion,
}) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [lengthHint, setLengthHint] = useState(false);
  // The suggestion row pending a delete confirmation, or null — mirrors
  // `SnippetPicker`'s own `pendingAction` shape (D5/D6).
  const [pendingDelete, setPendingDelete] = useState(null);

  const inputRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  useOutsideClose(open, [panelRef, inputRef], close);

  const anchor = useAnchoredRect(open, inputRef);
  const placed = open ? clampToViewport(anchor, { width: anchor?.width }) : null;
  const floating = !!placed;

  // Chips are DERIVED from `value` every render via the LOSSLESS parser
  // (bk-1295, B1) — no local chip state to fall out of sync, and a legacy
  // value that was never touched is simply re-parsed the same way on every
  // render without ever calling `onChange`. Duplicates and over-120 pieces
  // come back out exactly as stored.
  const items = splitStoredItems(value);

  const commitDraft = (raw) => {
    const text = String(raw || '');
    if (!text.trim()) { setDraft(''); return; }
    // LOSSLESS candidates (bk-1295, B1) — an over-120 piece is ADDED as a
    // chip, never dropped; the hint below only says it won't be learned.
    // Dedupe against the field's EXISTING chips (and within this one typed
    // batch) is still case-insensitive — a genuine new addition duplicating
    // an existing chip adds nothing, which is a UX nicety, not a B1
    // data-loss concern (only a LEGACY piece must never be dropped).
    const candidates = splitStoredItems(text);
    const hadOverLong = candidates.some((p) => p.length > SNIPPET_ITEM_MAX_LENGTH);
    const seen = new Set(items.map(normaliseItemKey));
    const newItems = [];
    candidates.forEach((item) => {
      const key = normaliseItemKey(item);
      if (seen.has(key)) return;
      seen.add(key);
      newItems.push(item);
    });
    if (newItems.length > 0) onChange(itemsToStoredValue([...items, ...newItems]));
    setDraft('');
    setLengthHint(hadOverLong);
  };

  const addSuggestion = (row) => {
    const text = (row && row.text) || '';
    if (!text) return;
    const key = normaliseItemKey(text);
    if (items.some((item) => normaliseItemKey(item) === key)) return;
    onChange(itemsToStoredValue([...items, text]));
  };

  // Removal is by INDEX (bk-1295, B1) — duplicates now round-trip as
  // distinct chips, so removing "the cough at position 2" must never also
  // remove "the cough at position 5"; a key-based filter would.
  const removeChip = (idx) => {
    onChange(itemsToStoredValue(items.filter((_, i) => i !== idx)));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDraft(draft);
    } else if (e.key === ',') {
      // Comma commits too (task spec) — the character is never inserted
      // into the input itself.
      e.preventDefault();
      commitDraft(draft);
    } else if (e.key === 'Escape') {
      close();
    }
  };

  const openPanel = () => {
    setOpen((prev) => {
      if (!prev && !loading && onOpenSuggestions) onOpenSuggestions();
      return true;
    });
  };

  const handleDeleteRequest = (row) => setPendingDelete(row);
  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await onDeleteSuggestion?.(pendingDelete.snippet_id);
    } finally {
      setPendingDelete(null);
    }
  };

  const trimmedQuery = query.trim();
  const searching = trimmedQuery.length > 0;
  const showSearchBox = suggestions.length > VISIBLE_ROW_CAP;
  const filteredSuggestions = searching ? filterItemRows(suggestions, trimmedQuery) : suggestions;
  const visibleSuggestions = (!searching && !expanded) ? filteredSuggestions.slice(0, VISIBLE_ROW_CAP) : filteredSuggestions;
  const hiddenCount = (!searching && !expanded) ? Math.max(filteredSuggestions.length - VISIBLE_ROW_CAP, 0) : 0;

  if (readOnly) {
    return (
      <div data-testid={`complaint-items-field-${fieldKey}`}>
        <div className="flex flex-wrap gap-1.5" data-testid="complaint-items-chip-row">
          {items.length === 0 && <span className="text-[13px] text-slate-800 dark:text-slate-100">—</span>}
          {items.map((item, idx) => {
            // bk-1295, B1 — an over-120 piece renders as a chip too (never
            // dropped): truncated visually with the full text in `title`.
            const overLimit = item.length > SNIPPET_ITEM_MAX_LENGTH;
            return (
              <span
                key={`${idx}-${item}`}
                data-testid={`complaint-chip-${item}`}
                title={overLimit ? item : undefined}
                className={`${readonlyChipCls}${overLimit ? ' max-w-[240px]' : ''}`}
              >
                <span className={overLimit ? 'truncate' : undefined}>{item}</span>
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`complaint-items-field-${fieldKey}`}>
      <div className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-2">
        <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="complaint-items-chip-row">
          {items.map((item, idx) => {
            // bk-1295, B1 — a piece over SNIPPET_ITEM_MAX_LENGTH is KEPT as
            // a chip (never dropped): truncated visually, the full text in
            // `title`, and a small "not saved as a suggestion" marker.
            const overLimit = item.length > SNIPPET_ITEM_MAX_LENGTH;
            return (
              <span
                key={`${idx}-${item}`}
                data-testid={`complaint-chip-${item}`}
                title={overLimit ? item : undefined}
                className={`${chipCls}${overLimit ? ' max-w-[240px]' : ''}`}
              >
                <span className={overLimit ? 'truncate' : undefined}>{item}</span>
                {overLimit && (
                  <span
                    data-testid={`complaint-chip-not-saved-${idx}`}
                    title={COMPLAINT_ITEM_NOT_SAVED_MARKER_COPY}
                    aria-label={COMPLAINT_ITEM_NOT_SAVED_MARKER_COPY}
                    className="shrink-0 text-[10px] font-bold text-teal-600/80 dark:text-teal-300/80"
                  >
                    !
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeChip(idx)}
                  aria-label={`Remove "${item}"`}
                  data-testid={`complaint-chip-remove-${item}`}
                  className="text-teal-700 dark:text-teal-200 font-bold leading-none px-1 shrink-0"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={draft}
            placeholder={COMPLAINT_ITEMS_PLACEHOLDER}
            onFocus={openPanel}
            onChange={(e) => { setDraft(e.target.value); setLengthHint(false); }}
            onKeyDown={handleKeyDown}
            data-testid="complaint-items-input"
            className="w-full px-2 py-1.5 border-0 outline-none bg-transparent text-slate-800 dark:text-slate-100 text-[13px]"
          />

          {open && (
            <div
              ref={panelRef}
              role="dialog"
              aria-label={`${fieldKey} suggestions`}
              data-testid="complaint-suggest-popover"
              data-floating={floating ? 'true' : 'false'}
              onKeyDown={(e) => { if (e.key === 'Escape') { close(); inputRef.current?.focus(); } }}
              className="z-[1000] rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg flex flex-col"
              style={{
                ...(floating ? {
                  position: 'fixed', top: placed.top, left: placed.left, width: Math.max(anchor?.width || 0, 280),
                } : { position: 'absolute', top: '100%', left: 0, width: 280 }),
                maxHeight: 'min(22rem, 60vh)',
              }}
            >
              <div className="px-2.5 py-2 border-b border-slate-200 dark:border-slate-600 shrink-0">
                <div data-testid="complaint-suggest-header" className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
                  {headerLine(ownerDoctorName)}
                </div>
                {showSearchBox && (
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={COMPLAINT_SUGGEST_SEARCH_PLACEHOLDER}
                    data-testid="complaint-suggest-search"
                    className="mt-1.5 w-full px-2 py-1 rounded-md border border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12px]"
                  />
                )}
              </div>

              <div className="overflow-y-auto flex-1" data-testid="complaint-suggest-list">
                {loading && (
                  <div data-testid="complaint-suggest-loading" className="px-2.5 py-4 text-[12px] text-slate-500 dark:text-slate-400 text-center">
                    {COMPLAINT_SUGGEST_LOADING_COPY}
                  </div>
                )}
                {!loading && error && (
                  <div data-testid="complaint-suggest-error" className="px-2.5 py-2 text-[12px] text-red-600 dark:text-red-400">
                    {error}
                  </div>
                )}
                {!loading && !error && suggestions.length === 0 && (
                  <div data-testid="complaint-suggest-empty" className="px-2.5 py-4 text-[12px] text-slate-500 dark:text-slate-400 text-center">
                    {COMPLAINT_SUGGEST_EMPTY_COPY}
                  </div>
                )}
                {!loading && !error && suggestions.length > 0 && (
                  <>
                    {searching && filteredSuggestions.length === 0 && (
                      <div data-testid="complaint-suggest-no-match" className="px-2.5 py-4 text-[12px] text-slate-500 dark:text-slate-400 text-center">
                        {`No complaints match "${trimmedQuery}".`}
                      </div>
                    )}
                    {visibleSuggestions.map((row) => (
                      <div key={row.snippet_id} className={rowCls} data-testid={`complaint-suggest-row-${row.snippet_id}`}>
                        <button
                          type="button"
                          className="flex-1 min-w-0 text-left text-[12.5px] text-slate-800 dark:text-slate-100 truncate"
                          data-testid={`complaint-suggest-add-${row.snippet_id}`}
                          onClick={() => addSuggestion(row)}
                        >
                          {row.text}
                        </button>
                        <button
                          type="button"
                          disabled={!row.can_delete}
                          title={row.can_delete ? undefined : complaintSuggestDeleteDisabledReason(ownerDoctorName)}
                          aria-label={row.can_delete ? `Delete "${row.text}"` : complaintSuggestDeleteDisabledReason(ownerDoctorName)}
                          className={deleteBtnCls}
                          data-testid={`complaint-suggest-delete-${row.snippet_id}`}
                          onClick={() => handleDeleteRequest(row)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {hiddenCount > 0 && (
                      <button
                        type="button"
                        data-testid="complaint-suggest-more"
                        onClick={() => setExpanded(true)}
                        className="w-full text-center px-2.5 py-2 text-[11.5px] font-semibold text-teal-700 dark:text-teal-300 bg-slate-50 dark:bg-slate-900"
                      >
                        {`+${hiddenCount} more`}
                      </button>
                    )}
                    {searching && (
                      <div data-testid="complaint-suggest-count" className="px-2.5 py-1.5 text-[10.5px] text-slate-400 dark:text-slate-500">
                        {`Showing ${filteredSuggestions.length} of ${suggestions.length}`}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {lengthHint && (
        <div className="text-[11px] text-amber-700 dark:text-amber-400 mt-1" data-testid="complaint-items-length-hint">
          {COMPLAINT_ITEMS_LENGTH_HINT_COPY}
        </div>
      )}
      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1" data-testid="complaint-items-helper">
        {complaintItemsHelperCopy(ownerDoctorName)}
      </div>

      <ConfirmationModal
        isOpen={!!pendingDelete}
        title="Delete this complaint?"
        message={pendingDelete ? deleteConfirmMessage(pendingDelete.text, ownerDoctorName) : ''}
        ctaLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        icon="🗑"
        autoFocusCancel
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
